import { useRef, useState } from 'react';

import { shouldShowMobileWeb } from '@confluence/mobile-detection';

// Hook that detects when the error page should use mobile layout
// Combines existing shouldShowMobileWeb() with container width detection (< 700px)
export const useErrorPageMobileLayout = (): {
	resizeContainerRef: (element: HTMLDivElement | null) => void;
	shouldUseMobileLayout: boolean;
} => {
	const MOBILE_BREAKPOINT = 700;
	const [isContainerNarrow, setIsContainerNarrow] = useState(false);
	const lastIsNarrowRef = useRef<boolean | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	const handleResize = ([entry]: ResizeObserverEntry[]) => {
		const nextIsNarrow = entry.contentRect.width < MOBILE_BREAKPOINT;
		if (lastIsNarrowRef.current !== nextIsNarrow) {
			lastIsNarrowRef.current = nextIsNarrow;
			setIsContainerNarrow(nextIsNarrow);
		}
	};

	const resizeContainerRef = (element: HTMLDivElement | null): void => {
		// Clean up previous observer
		if (resizeObserverRef.current) {
			resizeObserverRef.current.disconnect();
			resizeObserverRef.current = null;
		}

		// Set up new observer if element exists
		if (element) {
			const resizeObserver = new ResizeObserver(handleResize);
			resizeObserver.observe(element);
			resizeObserverRef.current = resizeObserver;
		}
	};

	// Combine existing mobile detection with container width detection
	const shouldUseMobileLayout = isContainerNarrow || shouldShowMobileWeb();

	return { resizeContainerRef, shouldUseMobileLayout };
};
